import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { DashboardLayout, Panel, StatCard, StatusBadge } from "@/components/DashboardLayout";
import { ProviderBookings } from "@/pages/Bookings";
import { api, apiError, naira } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

const EarningsPanel = () => {
  const [e, setE] = useState(null);
  const [escrow, setEscrow] = useState(null);

  useEffect(() => {
    api.get("/api/provider/earnings").then((r) => setE(r.data)).catch(() => {});
    api.get("/api/escrow/summary").then((r) => setEscrow(r.data)).catch(() => {});
  }, []);

  if (!e) return null;

  return (
    <Panel title="Earnings" description="Value of visits confirmed by clients and witnesses." testId="sp-earnings-panel">
      <div className="grid gap-4 sm:grid-cols-5">
        {[
          ["Confirmed visits", e.confirmed_visits, "sp-earn-confirmed"],
          ["Your share (97%)", naira(e.your_share_ngn ?? 0), "sp-earn-share"],
          ["Payout due", naira(e.payout_due_ngn ?? 0), "sp-earn-payout-due"],
          ["Held in escrow", naira(escrow?.in_escrow_ngn ?? 0), "sp-earn-escrow"],
          ["Released", naira(escrow?.released_ngn ?? 0), "sp-earn-released"],
        ].map(([label, value, testId]) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-emerald-700">{label}</p>
            <p className="mt-1.5 font-display text-xl font-extrabold text-slate-900" data-testid={testId}>{value}</p>
          </div>
        ))}
      </div>

      {e.monthly.length > 0 && (
        <ul className="mt-6 divide-y divide-slate-100" data-testid="sp-earnings-monthly">
          {e.monthly.map((m) => (
            <li key={m.month} className="flex items-center justify-between py-3 text-sm">
              <span className="text-slate-700">{m.month}</span>
              <span className="text-slate-500">{m.visits} visit{m.visits === 1 ? "" : "s"}</span>
              <span className="font-display font-bold text-emerald-800">{naira(m.earned_ngn)}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-4 text-xs text-slate-500">
        {e.pending_visits} visit{e.pending_visits === 1 ? "" : "s"} awaiting confirmation · average fee {naira(e.average_fee_ngn)}.
        Card payments are SIMULATED until live Paystack keys are added.
      </p>
    </Panel>
  );
};

export default function ProviderDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [services, setServices] = useState([]);
  const [availability, setAvailability] = useState({ slots: [], accepting_bookings: true });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", category: "GENERAL", fee_ngn: "", duration_minutes: 60 });

  const load = async () => {
    try {
      const p = await api.get("/api/provider/profile");
      setProfile(p.data.profile);
      if (p.data.profile) {
        const [s, a] = await Promise.all([api.get("/api/provider/services"), api.get("/api/provider/availability")]);
        setServices(s.data);
        setAvailability({ slots: a.data.slots || [], accepting_bookings: a.data.accepting_bookings ?? true });
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const addService = async () => {
    try {
      await api.post("/api/provider/services", { ...form, fee_ngn: Number(form.fee_ngn), duration_minutes: Number(form.duration_minutes) });
      toast.success("Service added");
      setOpen(false);
      setForm({ name: "", description: "", category: "GENERAL", fee_ngn: "", duration_minutes: 60 });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeService = async (id) => {
    try {
      await api.delete(`/api/provider/services/${id}`);
      toast.success("Service removed");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleDay = async (day) => {
    const exists = availability.slots.some((s) => s.day === day);
    const slots = exists
      ? availability.slots.filter((s) => s.day !== day)
      : [...availability.slots, { day, start_time: "09:00", end_time: "17:00" }];
    try {
      await api.put("/api/provider/availability", { slots, accepting_bookings: availability.accepting_bookings });
      setAvailability({ ...availability, slots });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const status = profile?.verification_status || user?.kyc_status;

  return (
    <DashboardLayout
      title={`Welcome, ${user?.full_name?.split(" ")[0]}`}
      subtitle="Provider dashboard — verification, services, fees and availability."
      badge={<StatusBadge status={status} testId="sp-status-badge" />}
    >
      <div className="grid gap-6">
        {!profile && (
          <Panel title="Submit your verification" description="Licence and identity documents are required before your profile can be listed." testId="sp-kyc-panel">
            <Button asChild className="rounded-full bg-emerald-900 hover:bg-emerald-800">
              <Link to="/onboarding/provider" data-testid="sp-start-kyc-btn">Start verification</Link>
            </Button>
          </Panel>
        )}

        {profile && (
          <>
            <div className="grid gap-4 sm:grid-cols-3">
              <StatCard label="Verification" value={status?.replaceAll("_", " ")} hint={status === "VERIFIED" ? "Listed in marketplace" : "Not listed yet"} testId="sp-stat-status" />
              <StatCard label="Client rating" value={profile.rating_count ? `${Number(profile.rating_average).toFixed(1)} ★` : "—"} hint={`${profile.rating_count || 0} rating${profile.rating_count === 1 ? "" : "s"}`} testId="sp-stat-rating" />
              <StatCard label="Services" value={services.length} testId="sp-stat-services" />
              <StatCard label="Registration" value={`${profile.registration_body} · ${profile.registration_number}`} testId="sp-stat-registration" />
            </div>

            <Panel
              title="Services & fees"
              description="Fees are shown to verified clients in the marketplace."
              testId="sp-services-panel"
              action={
                <Dialog open={open} onOpenChange={setOpen}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full bg-emerald-900 hover:bg-emerald-800" data-testid="sp-add-service-btn">
                      <Plus className="mr-2 h-4 w-4" /> Add service
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>New service</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                      <div><Label>Service name</Label><Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sp-service-name-input" /></div>
                      <div><Label>Description</Label><Textarea className="mt-1.5" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="sp-service-desc-input" /></div>
                      <div><Label>Category</Label><Input className="mt-1.5" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="sp-service-category-input" /></div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>Fee (₦)</Label><Input type="number" className="mt-1.5" value={form.fee_ngn} onChange={(e) => setForm({ ...form, fee_ngn: e.target.value })} data-testid="sp-service-fee-input" /></div>
                        <div><Label>Duration (mins)</Label><Input type="number" className="mt-1.5" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} data-testid="sp-service-duration-input" /></div>
                      </div>
                      <Button className="w-full rounded-full bg-emerald-900" onClick={addService} data-testid="sp-service-save-btn">Save service</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              }
            >
              {services.length ? (
                <ul className="divide-y divide-slate-100" data-testid="sp-services-list">
                  {services.map((s) => (
                    <li key={s.id} className="flex flex-wrap items-center justify-between gap-3 py-4">
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900">{s.name}</p>
                        <p className="text-xs text-slate-500">{s.category} · {s.duration_minutes} mins</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-display font-bold text-emerald-800">{naira(s.fee_ngn)}</span>
                        <Button size="icon" variant="ghost" onClick={() => removeService(s.id)} data-testid={`sp-delete-service-${s.id}`}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-500">No services yet. Add your first service and fee.</p>
              )}
            </Panel>

            <Panel title="Availability" description="Select the days you accept home visits." testId="sp-availability-panel">
              <div className="flex flex-wrap gap-2">
                {DAYS.map((d) => {
                  const on = availability.slots.some((s) => s.day === d);
                  return (
                    <button
                      key={d}
                      onClick={() => toggleDay(d)}
                      data-testid={`sp-day-${d.toLowerCase()}`}
                      className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
                        on ? "border-emerald-600 bg-emerald-900 text-white" : "border-slate-200 bg-white text-slate-600"
                      }`}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
              <p className="mt-3 text-xs text-slate-500">Selected days run 09:00–17:00. Detailed time editing arrives in Stage 2.</p>
            </Panel>
          </>
        )}

        {profile && status === "VERIFIED" && <EarningsPanel />}
        {profile && status === "VERIFIED" && <ProviderBookings embedded />}      </div>
    </DashboardLayout>
  );
}
